import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import IcomoonReact from 'icomoon-react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'emotion-theming';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { TransitionGlobal } from '../../../../shared/GlobalStyles';
import StaticAddColumnDropdownComponent from './StaticAddColumnDropdownComponent';
import StatusBadgeScroller from './StatusBadgeScroller';
import TBportalTooltip from '../../../../shared/components/TBportalTooltip';
import Text from '../../../../shared/components/Text/Text';
import {
  updateProgrammedCalendarItem,
  updateSavedWorkoutItem,
  updateReduxCalendarItem,
  updateReduxProgramItem,
  swapProgrammedItemOnChildCalendar,
  setCombineWithInfo,
} from '../../ducks/calendarActions';
import SwapPropDropdownSAQC from './SwapPropDropdownSAQC';
import capitalizeWords from '../../../../shared/utils/capitalizeWords';
import useSessionState from './hooks/useSessionState';
import { getDebouncedHandlerForTable, flushDebounceForTable } from './hooks/debounceHandler';
import { useFlushDebounceOnMouseMove } from './hooks/useFlushDebounceOnMouseMove';
import { normalizeRepToken } from '../../../../shared/utils/normalizeRepToken';
import wipeRepKeywordOnMutation from '../../../../shared/utils/wipeRepKeywordOnMutation';
import {
  mapLegacyTextColor, StateBadge, SetsStepper, SetsCountLabel, SetsSubtext, SetsFieldGroup,
} from './TransposedTableShared';
import { FOCUS_FILL_VAR } from '../../../../shared/theme/focusStyles';

const RANGE_FIELDS = new Set(['prescribed value']);

const Wrapper = styled('div')`
  display: flex;
  width: 100%;
  flex-direction: column;
`;

const RowValue = styled('input')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  color: ${({ theme }) => theme.colors.text};
  height: 32px;
  background: ${(props) => props.background || props.theme.colors.surfaceInput};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  text-align: center;
  border-radius: 4px;
  color: ${mapLegacyTextColor};

  ::placeholder {
    color: ${mapLegacyTextColor};
  }
`;

const RowValueContainer = styled('div')`
  width: 100%;
  min-width: 180px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const NameAndDropdown = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 3px;
`;

/** Transposed-layout styled components — properties as rows, sets as columns. */
const SetsCountHeader = styled('div')`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 20px 0 22px 6px;
`;

const StepperButton = styled('button')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text};
  font-family: 'Nunito Sans';
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  transition: ${TransitionGlobal};
  &:hover:not(:disabled) { background: ${({ theme }) => theme.colors.surfaceHoverStrong}; }
  &:focus { outline: none; }
  &:focus-visible {
    outline: none;
    background: ${FOCUS_FILL_VAR};
    box-shadow: inset 0 0 0 2px #BFDBFE;
  }
  &:disabled {
    color: ${({ theme }) => theme.colors.textDisabled};
    cursor: not-allowed;
  }
`;

const StepperInput = styled('input')`
  width: 35px;
  border: none;
  border-left: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-right: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: transparent;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &:focus { outline: none; }
`;

const TransposedScroll = styled('div')`
  width: 100%;
  overflow-x: auto;
  padding-left: 6px;
`;

const TransposedTable = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const TransposedHeaderRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  width: 100%;
`;

const TransposedRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0;
  width: 100%;
`;

const PropertyLabelCell = styled('div')`
  display: flex;
  align-items: center;
  min-width: 120px;
  max-width: 120px;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text};
`;

const SetHeaderCell = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 80px;
  min-width: 80px;
  font-family: 'Nunito Sans';
  font-size: 13px;
  font-weight: 400;
  color: ${({ theme }) => (theme.isDark ? theme.colors.textSecondary : theme.colors.text)};
`;

const SetCell = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 80px;
  min-width: 80px;
  > div {
    min-width: 0;
    width: 100%;
  }
  input {
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
  }
`;

const TransposedFooter = styled('div')`
  display: flex;
  align-items: center;
  padding-top: 12px;
`;

const AddPropertyTrigger = styled('div')`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px dashed ${({ theme }) => theme.colors.borderMedium};
  border-radius: 4px;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  background: transparent;
  cursor: pointer;
  user-select: none;
  transition: ${TransitionGlobal};
  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHoverStrong};
    border-color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const AddPropertyTriggerIcon = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.buttonPrimaryBg};
`;

const TooltipDataContainer = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: 10px;
  padding-right: 16px;
  padding-left: 16px;
  gap: 10px;
  border-radius: 4px;
  max-width: 250px;
  min-width: 250px;
`;

const TooltipDataContainerAthleteInput = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-right: 16px;
  padding-left: 16px;
  gap: 10px;
  border-radius: 4px;
  max-width: 250px;
`;

const ExerciseTableTransposedSAQC = ({
  content,
  index,
  coachCompletion,
  completionOnly,
  swapDropdownSelectedExercise,
  nonSwappedItemFromParentCalendar,
  isRecurring = '',
  assignedPace,
  measure,
  date,
  dayNumber,
}) => {
  const { colors } = useTheme();
  const targetRef = useRef(null);
  const targetRefRest = useRef(null);
  const targetRefAthleteInputOnly = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showAthleteInputOnlyTooltip, setShowAthleteInputOnlyTooltip] = useState(false);
  const [showTooltipRest, setShowTooltipRest] = useState(false);
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const calendarContainerScrollPositionTop = useSelector(
    (state) => state.calendar.data.calendarContainerScrollPositionTop,
  );
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const effectiveDate = date || selectedDate;
  const endpointFormattedDate = moment(effectiveDate).format('YYYY-MM-DD');
  const combineWithInfo = useSelector((state) => state.calendar.data.combineWithInfo);
  const focusTarget = useSelector((state) => state.calendar.data.focusTarget);

  const [rest, setRest] = useState(null);
  const [intensityUnit, setIntensityUnit] = useState(null);
  const [restPerRepUnit, setRestPerRepUnit] = useState(null);

  const [firstPrimaryRowSwitchLoad, setFirstPrimaryRowSwitchLoad] = useState(true);
  const [firstContentLoad, setFirstContentLoad] = useState(true);
  const [firstLoadDropdownOptions, setFirstLoadDropdownOptions] = useState(true);

  const intensityKey = `intensity (${content.repIntensityUnit || 'bpm'})`;
  const restPerRepKey = `rest per rep (${content.repRestUnit || 'secs'})`;
  /** remove athlete input and prescribed value from initial state of inputs if
   * exercie is completion only
   */
  const primaryRowStateInitialStateIfExerciseIsCompletionOnly = [
    { name: 'sets' },
    { name: 'rest', label: 'Rest (s)' },
    { name: 'reps' },
    { name: `rest per rep (${content.repRestUnit || 'secs'})` },
    { name: `intensity (${content.repIntensityUnit || 'bpm'})` },
  ];

  const [primaryRows, setPrimaryRows] = useState(content?.exercise?.inputType === 'C' ? primaryRowStateInitialStateIfExerciseIsCompletionOnly : [
    { name: 'sets' },
    { name: 'rest', label: 'Rest (s)' },
    { name: 'athlete input', label: `${capitalizeWords(content?.exercise?.inputTypeDescription)} (${content?.exercise?.unit === 'MS' ? 'm:s' : content?.exercise?.unitAbbr})` },
    { name: 'prescribed value', label: 'Prescribed Value' },
    { name: 'reps' },
    { name: `rest per rep (${content.repRestUnit || 'secs'})` },
    { name: `intensity (${content.repIntensityUnit || 'bpm'})` },
  ]);

  /** remove athlete input and prescribed value from initial state of switch options if
   * exercie is completion only
   */
  const defaultPrimaryRowSwitch = content?.exercise?.inputType === 'C'
    ? {
      sets: true,
      rest: false,
      reps: false,
      [restPerRepKey]: false,
      [intensityKey]: false,
    }
    : {
      sets: true,
      rest: false,
      'athlete input': true,
      'prescribed value': false,
      reps: false,
      [restPerRepKey]: false,
      [intensityKey]: false,
    };

  /** we need to use session storage for primary row switch values
   * so that if the user adds a field it will not get wiped out
   * between debounces if they haven't entered a value yet,
   * changes to individual table properties will persist the session
   */
  const [primaryRowSwitch, setPrimaryRowSwitchState] = useSessionState(
    'primaryRowSwitchSAQCTable',
    content?.assignedId,
    defaultPrimaryRowSwitch,
  );

  const keySwapDictionary = {
    'prescribed value': 'prescribedValue',
    set: 'setNumber',
    reps: 'reps',
    'intensity (bpm)': 'intensityValue',
    'intensity (RPE)': 'intensityValue',
    'intensity (%)': 'intensityValue',
    'intensity (W)': 'intensityValue',
    'rest per rep (secs)': 'restPerRep',
    'rest per rep (bpm)': 'restPerRep',
  };

  const [rows, setRows] = useState(content?.sets || []);

  /** Draft string shown while the Sets field is focused. The real set count is
   * committed once on blur/Enter so typing a two-digit count never transiently
   * collapses the table and wipes per-set values. null = not editing. */
  const [setsDraft, setSetsDraft] = useState(null);

  // Ref for the table container, used to scope keyboard shortcuts
  const tableContainerRef = useRef(null);

  const hasMounted = useRef(false);

  /** Re-measure the virtualized list cell on every vertical-height
   * change. In the transposed layout height is driven by property
   * visibility (primaryRowSwitch: add/remove rows) and the status pills
   * wrapping; rows is kept too (harmless — set count is horizontal). */
  useEffect(() => {
    if (!hasMounted.current) {
      /** skip first run because cell measurer wrapper
       * will get that and we don't want to run it twice */
      hasMounted.current = true;
      return;
    }
    if (measure) {
      measure();
    }
  }, [rows, primaryRowSwitch, coachCompletion, completionOnly]);

  const typeFormatter = (typeDescription) => {
    if (typeDescription === 'lift') {
      return 'lift';
    }
    if (typeDescription === 'cardio') {
      return 'saq+c';
    }
    if (typeDescription === 'circuit') {
      return 'circuit';
    }
    return '';
  };

  const dispatch = useDispatch();

  const formatDate = (dateValue) => `${moment(dateValue).format('YYYY-MM-DD')}`;

  /** debounced function will maintain values at the time of creation
   * on mount unless passed updated values, we can't to make sure we are
   * always using the most updated selected date and current user
   */
  const currentUserRef = useRef(currentUser);
  const selectedDateRef = useRef(effectiveDate);
  const dayNumberRef = useRef(dayNumber);
  useEffect(() => {
    currentUserRef.current = currentUser;
    selectedDateRef.current = effectiveDate;
    dayNumberRef.current = dayNumber;
  }, [currentUser, effectiveDate, dayNumber]);

  /** An item inherited from a parent calendar detaches onto the child on its
   * first edit, so the 4s debounce is flushed as soon as the coach moves on —
   * otherwise a pending save can land after a drag and fight the new order. */
  useFlushDebounceOnMouseMove(content.assignedId, nonSwappedItemFromParentCalendar);

  const debouncedHandleUpdateSAQCItem = getDebouncedHandlerForTable(content.assignedId, (
    updatedRows,
    dbrest,
    dbPaceUnit,
    dbIntensityUnit,
    dbCoachCompletion,
    dbCompletionOnly,
    dbContent,
    dbIndex,
    dbNonSwappedItemFromParentCalendar,
    dbRestUnit,
    reorder = false,
  ) => {
    const prunedArray = updatedRows.map(({
      prescribedType, percent, ...remainder
    }) => ({
      ...remainder,
    }));
    const updateItem = {
      assignedId: dbContent.assignedId,
      exerciseId: dbContent.exerciseId,
      order: dbIndex,
      sets: prunedArray,
      groupingLetter: dbContent.groupingLetter,
      additionalInfo: dbContent.additionalInfo || null,
      coachCompletion: dbCoachCompletion,
      completionOnly: dbCompletionOnly,
      description: dbContent?.description || null,
      type: typeFormatter(dbContent?.exercise?.typeDescription),
    };
    if (programType === 'calendar') {
      if (!isRecurring) {
        updateItem.date = moment(effectiveDate).format('YYYY-MM-DD');
      } else {
        updateItem.isRecurring = isRecurring;
      }
    }
    if (programType === 'free-form-program') {
      updateItem.dayNumber = dayNumberRef.current;
    }
    if (dbrest) {
      updateItem.restTime = dbrest;
    }
    if (dbPaceUnit) {
      updateItem.pacing = dbPaceUnit;
    }
    if (dbIntensityUnit) {
      updateItem.repIntensityUnit = dbIntensityUnit;
    }
    if (dbRestUnit) {
      updateItem.repRestUnit = dbRestUnit;
    }
    if (programType === 'free-form-program') {
      dispatch(updateSavedWorkoutItem(
        currentUserRef.current.accountCode,
        updateItem,
        dbIndex,
        dayNumberRef.current,
        reorder,
      ));
    }
    if (programType === 'calendar') {
      const formattedDate = formatDate(selectedDateRef.current);
      if (dbNonSwappedItemFromParentCalendar) {
        dispatch(swapProgrammedItemOnChildCalendar(
          currentUserRef.current.accountCode,
          updateItem,
          dbIndex,
          formattedDate,
          reorder,
        ));
      } else {
        dispatch(updateProgrammedCalendarItem(
          currentUserRef.current.accountCode,
          updateItem,
          dbIndex,
          formattedDate,
          reorder,
        ));
      }
    }
  });

  /** This useEffect listens for changes is the exercise chosen from the
     * table dropdown menu that can be used to change the exericse
     * it immediately update the item in redux so we see the change in the table
     * immediately, then we call the debouce update the same way we do
     * for every other input change
     */
  useEffect(() => {
    if (swapDropdownSelectedExercise) {
      const prunedArray = rows.map(({ prescribedType, percent, ...remainder }) => remainder);
      const updatedItem = {
        ...content,
        description: swapDropdownSelectedExercise?.description,
        additionalInfo: swapDropdownSelectedExercise?.additionalInfo,
        assignedId: content.assignedId,
        date: programType === 'free-form-program' ? '' : formatDate(effectiveDate),
        exerciseId: swapDropdownSelectedExercise.exerciseId,
        exercise: swapDropdownSelectedExercise,
        title: swapDropdownSelectedExercise?.name,
        order: index,
        sets: prunedArray,
        coachCompletion,
        completionOnly,
        type: typeFormatter(swapDropdownSelectedExercise.typeDescription),
      };
      if (rest) {
        updatedItem.restTime = rest;
      }
      if (assignedPace) {
        updatedItem.pacing = assignedPace;
      }
      if (intensityUnit) {
        updatedItem.repIntensityUnit = intensityUnit;
      }
      if (restPerRepUnit) {
        updatedItem.repRestUnit = restPerRepUnit;
      }
      if (programType === 'free-form-program') {
        dispatch(updateReduxProgramItem(updatedItem, dayNumber || selectedProgramDayNumber, index));
      } else {
        dispatch(updateReduxCalendarItem(updatedItem, endpointFormattedDate, index));
      }
      debouncedHandleUpdateSAQCItem.cancel();
      debouncedHandleUpdateSAQCItem(
        rows,
        rest,
        assignedPace,
        intensityUnit,
        coachCompletion,
        completionOnly,
        updatedItem,
        index,
        nonSwappedItemFromParentCalendar,
        restPerRepUnit,
      );
      flushDebounceForTable(content.assignedId);
    }
  }, [swapDropdownSelectedExercise]);

  useEffect(() => {
    const isSource = combineWithInfo?.source?.length
      ? combineWithInfo.source.some(
        (item) => item?.assignedId.toString() === content?.assignedId.toString(),
      )
      : false;

    const sourceItem = combineWithInfo?.source?.find(
      (item) => item?.assignedId.toString() === content?.assignedId.toString(),
    );

    const isTarget = combineWithInfo?.target?.assignedId === content?.assignedId;

    const targetItem = combineWithInfo?.target;

    if (combineWithInfo && ((isSource) || (isTarget))) {
      dispatch(setCombineWithInfo(null));

      let groupingLetter = content?.groupingLetter;
      if (isSource) {
        groupingLetter = sourceItem?.groupingLetter;
      } else if (isTarget) {
        groupingLetter = targetItem?.groupingLetter;
      }

      let groupingColorCode = content?.groupingColorCode;
      if (isSource) {
        groupingColorCode = sourceItem?.groupingColorCode;
      } else if (isTarget) {
        groupingColorCode = targetItem?.groupingColorCode;
      }

      const updatedItem = {
        ...content,
        date: formatDate(effectiveDate),
        order: index,
        sets: rows,
        groupingLetter,
        groupingColorCode,
        coachCompletion,
        completionOnly,
      };
      // Combining into a superset has nothing to do with these fields, so a
      // combine must never look like "the coach cleared this value." Local
      // state can be momentarily unavailable when this fires (component
      // remount/render-order race during the drag-drop reorder), so fall
      // back to what's already persisted on `content` rather than letting
      // the field drop out of the request — the update endpoint treats an
      // absent key as an explicit clear.
      const resolvedRest = rest || content.restTime || null;
      const resolvedPace = assignedPace || content.pacing || null;
      const resolvedIntensityUnit = intensityUnit || content.repIntensityUnit || null;
      const resolvedRestUnit = restPerRepUnit || content.repRestUnit || null;
      if (resolvedRest) {
        updatedItem.restTime = resolvedRest;
      }
      if (resolvedPace) {
        updatedItem.pacing = resolvedPace;
      }
      if (resolvedIntensityUnit) {
        updatedItem.repIntensityUnit = resolvedIntensityUnit;
      }
      if (resolvedRestUnit) {
        updatedItem.repRestUnit = resolvedRestUnit;
      }
      const reorder = true;
      debouncedHandleUpdateSAQCItem(
        rows,
        resolvedRest,
        resolvedPace,
        resolvedIntensityUnit,
        coachCompletion,
        completionOnly,
        updatedItem,
        index,
        nonSwappedItemFromParentCalendar,
        resolvedRestUnit,
        reorder,
      );
      flushDebounceForTable(content.assignedId);
    }
  }, [combineWithInfo]);

  // Manages focus on input fields when focusTarget changes.
  // If focusTarget's assignedId matches this content's
  // assignedId, finds and focuses the matching input element
  // after a short delay to ensure the DOM is ready.
  useEffect(() => {
    if (focusTarget?.assignedId === content.assignedId) {
      const firstInput = document.querySelector(`[data-focus-target="${content.assignedId}"]`);
      if (firstInput) {
        setTimeout(() => {
          firstInput.focus();
        }, 100); // Small delay to ensure DOM is ready
      }
    }
  }, [focusTarget, content.assignedId]);

  useEffect(() => {
    if (!firstLoadDropdownOptions) {
      debouncedHandleUpdateSAQCItem(
        rows,
        rest,
        assignedPace,
        intensityUnit,
        coachCompletion,
        completionOnly,
        content,
        index,
        nonSwappedItemFromParentCalendar,
        restPerRepUnit,
      );
    } else {
      setFirstLoadDropdownOptions(false);
    }
  }, [coachCompletion, completionOnly, assignedPace]);

  const setRestFunction = (value) => {
    const intRest = parseInt(value, 10);
    // Prevent negative values
    if (intRest < 0) return;
    setRest(intRest);
    debouncedHandleUpdateSAQCItem(
      rows,
      intRest,
      assignedPace,
      intensityUnit,
      coachCompletion,
      completionOnly,
      content,
      index,
      nonSwappedItemFromParentCalendar,
      restPerRepUnit,
    );
  };

  const setIntensityUnitAndUpdate = (value) => {
    setIntensityUnit(value);
    debouncedHandleUpdateSAQCItem(
      rows,
      rest,
      assignedPace,
      value,
      coachCompletion,
      completionOnly,
      content,
      index,
      nonSwappedItemFromParentCalendar,
      restPerRepUnit,
    );
  };

  const setRestPerRepUnitAndUpdate = (value) => {
    setRestPerRepUnit(value);
    debouncedHandleUpdateSAQCItem(
      rows,
      rest,
      assignedPace,
      intensityUnit,
      coachCompletion,
      completionOnly,
      content,
      index,
      nonSwappedItemFromParentCalendar,
      value,
    );
  };

  // Track if all optional columns are enabled to hide the add column button
  const [showAddColumnButton, setShowAddColumnButton] = useState(true);

  // Hide add column button when all optional properties are enabled
  useEffect(() => {
    const allOptionalPropertiesEnabled = primaryRowSwitch.rest
      && primaryRowSwitch['prescribed value']
      && primaryRowSwitch.reps
      && (primaryRowSwitch['rest per rep (secs)'] || primaryRowSwitch['rest per rep (bpm)'])
      && (primaryRowSwitch['intensity (bpm)'] || primaryRowSwitch['intensity (RPE)'] || primaryRowSwitch['intensity (%)'] || primaryRowSwitch['intensity (W)']);

    setShowAddColumnButton(!allOptionalPropertiesEnabled);
  }, [
    primaryRowSwitch.rest,
    primaryRowSwitch['prescribed value'],
    primaryRowSwitch.reps,
    primaryRowSwitch['rest per rep (secs)'],
    primaryRowSwitch['rest per rep (bpm)'],
    primaryRowSwitch['intensity (bpm)'],
    primaryRowSwitch['intensity (RPE)'],
    primaryRowSwitch['intensity (%)'],
    primaryRowSwitch['intensity (W)'],
  ]);

  // this is the function that updates the non-set-based values when columns are added
  const setPrimaryRowSwitch = (newState) => {
    setPrimaryRowSwitchState(newState);
    if (!firstPrimaryRowSwitchLoad) {
      setFirstPrimaryRowSwitchLoad(false);
      const initializedRows = [...rows];
      const updatedRows = initializedRows.map((row) => {
        const cleanedRow = { ...row };
        Object.keys(newState).forEach((key) => {
          if (!newState[key]) {
            delete cleanedRow[keySwapDictionary[key]];
          }
        });
        return cleanedRow;
      });
      setRows(updatedRows);
      let newRestValue;
      if (!newState.rest) {
        setRest(null);
        newRestValue = null;
      } else {
        newRestValue = rest;
      }

      let newIntensityUnitValue;
      if (!newState['intensity (bpm)'] && !newState['intensity (RPE)'] && !newState['intensity (%)'] && !newState['intensity (W)']) {
        setIntensityUnit(null);
        newIntensityUnitValue = null;
      } else if (newState['intensity (bpm)']) {
        newIntensityUnitValue = 'bpm';
      } else if (newState['intensity (RPE)']) {
        newIntensityUnitValue = 'RPE';
      } else if (newState['intensity (%)']) {
        newIntensityUnitValue = '%';
      } else if (newState['intensity (W)']) {
        newIntensityUnitValue = 'W';
      }

      let newRestUnitValue;
      if (!newState['rest per rep (secs)'] && !newState['rest per rep (bpm)']) {
        setRestPerRepUnit(null);
        newRestUnitValue = null;
      } else if (newState['rest per rep (secs)']) {
        newRestUnitValue = 'secs';
      } else if (newState['rest per rep (bpm)']) {
        newRestUnitValue = 'bpm';
      }
      debouncedHandleUpdateSAQCItem(
        updatedRows,
        newRestValue,
        assignedPace,
        newIntensityUnitValue,
        coachCompletion,
        completionOnly,
        content,
        index,
        nonSwappedItemFromParentCalendar,
        newRestUnitValue,
      );
    }
    setFirstPrimaryRowSwitchLoad(false);
  };

  // this is the function that sets the appropriate columns and values on load
  useEffect(() => {
    if (content && content.sets) {
      setRows(content.sets);
      const newPrimaryRowSwitch = { ...defaultPrimaryRowSwitch, ...primaryRowSwitch };

      // Track if any sets have intensity data
      let hasIntensityData = false;

      content?.sets.forEach((set) => {
        if (set.prescribedValue !== null && set.prescribedValue !== undefined && set.prescribedValue !== '') {
          newPrimaryRowSwitch['prescribed value'] = true;
        }
        if (set.restPerRep !== null && set.restPerRep !== undefined && set.restPerRep !== '') {
          newPrimaryRowSwitch[restPerRepKey] = true;
        }
        if (set.reps !== null && set.reps !== undefined && set.reps !== '') {
          newPrimaryRowSwitch.reps = true;
        }
        if (set.intensityValue !== null && set.intensityValue !== undefined && set.intensityValue !== '') {
          hasIntensityData = true;
        }
      });
      if (content) {
        if (content.restTime) {
          newPrimaryRowSwitch.rest = true;
          setRest(content.restTime);
        }

        if (content.repIntensityUnit || hasIntensityData) {
          const switchIntensityValue = intensityKey;
          newPrimaryRowSwitch[switchIntensityValue] = true;
          if (content.repIntensityUnit) {
            setIntensityUnit(content.repIntensityUnit);
          }
        }

        if (content.repRestUnit) {
          setRestPerRepUnit(content.repRestUnit);
        }
      }
      if (firstContentLoad) {
        setPrimaryRowSwitch(newPrimaryRowSwitch);
        setFirstContentLoad(false);
      }
      /** ADDED FOR SWAPPING EXERCISE NAME - NEED TO UPDATE SETS AND REPS WHEN CONTNENT CHANGES */
      setPrimaryRowSwitchState(newPrimaryRowSwitch);
    }
    /** ADDED FOR SWAPPING EXERCISE NAME - NEED TO UPDATE SETS AND REPS WHEN CONTNENT CHANGES */
    setPrimaryRows(content?.exercise?.inputType === 'C' ? primaryRowStateInitialStateIfExerciseIsCompletionOnly : [
      { name: 'sets' },
      { name: 'rest', label: 'Rest (s)' },
      { name: 'athlete input', label: `${capitalizeWords(content?.exercise?.inputTypeDescription)} (${content?.exercise?.unit === 'MS' ? 'm:s' : content?.exercise?.unitAbbr})` },
      { name: 'prescribed value', label: 'Prescribed Value' },
      { name: 'reps' },
      { name: `rest per rep (${content.repRestUnit || 'secs'})` },
      { name: `intensity (${content.repIntensityUnit || 'bpm'})` },
    ]);
  }, [content]);

  const updateColumnName = useCallback((oldName, newName) => {
    setPrimaryRows(
      (prevRows2) => prevRows2.map(
        (row) => (row.name === oldName ? { ...row, name: newName } : row),
      ),
    );

    setPrimaryRowSwitch((prevSwitch) => ({
      ...prevSwitch,
      [oldName]: false,
      [newName]: true,
    }));
  }, []);

  /** Transposed-layout: set the count directly (replace-on-type, 1–10).
   * Grows by cloning the last row, shrinks by slicing. */
  const setSetCount = (target) => {
    const clamped = Math.max(1, Math.min(10, target));
    if (clamped === rows.length) return;
    let updatedRows;
    if (clamped > rows.length) {
      const lastRow = rows[rows.length - 1] || {};
      const additions = Array.from(
        { length: clamped - rows.length },
        (_, i) => ({ ...lastRow, setNumber: rows.length + i + 1 }),
      );
      updatedRows = [...rows, ...additions];
    } else {
      updatedRows = rows.slice(0, clamped).map((row, i) => ({ ...row, setNumber: i + 1 }));
    }
    setRows(updatedRows);
    debouncedHandleUpdateSAQCItem(
      updatedRows,
      rest,
      assignedPace,
      intensityUnit,
      coachCompletion,
      completionOnly,
      content,
      index,
      nonSwappedItemFromParentCalendar,
      restPerRepUnit,
    );
  };

  const commitSetsDraft = () => {
    if (setsDraft !== null && setsDraft !== '') {
      setSetCount(Math.min(10, Math.max(1, parseInt(setsDraft, 10))));
    }
    setSetsDraft(null);
  };

  /** Auto-focus the first input of a newly added property (skips
   * disabled / athlete-input-only fields, which carry no data-add-focus). */
  const focusAddedProperty = useCallback((name) => {
    setTimeout(() => {
      const el = document.querySelector(`[data-add-focus="${content.assignedId}-${name}"]`);
      if (el && !el.disabled) {
        el.focus();
        if (typeof el.select === 'function') el.select();
      }
    }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content.assignedId]);

  const parseFieldValue = (field, value) => {
    if (RANGE_FIELDS.has(field) || field.includes('rest per rep')) {
      // Send range/+ tokens as strings; pure numbers as floats
      if (!value && value !== 0) return '';
      if (/^\d+(\.\d+)?$/.test(String(value))) return parseFloat(value);
      return value;
    }
    return value;
  };

  const handleInputChange = (index2, field, value) => {
    // Prevent negative values for numeric fields
    if ((field === 'prescribed value' || field.includes('rest per rep') || field === 'reps' || field.includes('intensity')) && parseFloat(value) < 0) {
      return;
    }

    // Prevent negative values for reps field
    let normalizedFieldValue = value;
    if (field === 'reps') {
      // Allow AMAP
      if (value && value.toUpperCase() === 'AMAP') {
        // AMAP is valid, continue
      } else if (value && value.includes('-') && !value.startsWith('-')) {
        // Check for ranges (e.g., "8-12") but not negative numbers
        const parts = value.split('-');
        if (parts.length === 2) {
          const start = parseInt(parts[0], 10);
          const end = parseInt(parts[1], 10);
          if (start < 0 || end < 0) {
            return; // Prevent negative values in ranges
          }
        }
      } else if (value && value.includes('+') && !value.startsWith('-')) {
        // Check for minimum values (e.g., "8+") but not negative numbers
        const numPart = value.replace('+', '');
        if (parseInt(numPart, 10) < 0) {
          return; // Prevent negative minimum values
        }
      } else if (value && !Number.isNaN(Number(value))) {
        // Check for simple numbers (including negative ones)
        if (parseFloat(value) < 0) {
          return; // Prevent negative simple numbers
        }
      }

      // Normalize AMAP case-drift ("Amap" -> "AMAP"). Invalid keyword variants
      // ("AMRAP") are caught at the row-level gate below to block the API call.
      if (value !== '' && value !== null && value !== undefined) {
        const tokenResult = normalizeRepToken(String(value), ['AMAP']);
        if (tokenResult.isValid) {
          normalizedFieldValue = tokenResult.value;
        }
      }
    }

    setRows((prevRows2) => {
      const fieldKey = keySwapDictionary[field];

      // Use normalized value (e.g., "Amap" -> "AMAP") for storage and submission
      const writeValue = field === 'reps' ? normalizedFieldValue : value;

      // Check if this is the first set and we should auto-fill
      const shouldAutoFill = index2 === 0 && writeValue !== null && writeValue !== undefined && writeValue !== '';

      let updatedRows;
      if (shouldAutoFill) {
        // Get the current value in the first set
        const currentFirstSetValue = prevRows2[0][fieldKey];

        // Check if all other sets for this field are empty
        // or have the same value as the current first set
        const otherSetsAreEmptyOrMatchCurrent = prevRows2.slice(1).every((row) => {
          const cellValue = row[fieldKey];
          return cellValue === null || cellValue === undefined || cellValue === '' || cellValue === currentFirstSetValue;
        });

        if (otherSetsAreEmptyOrMatchCurrent) {
          // Auto-fill all sets with the same value
          updatedRows = prevRows2.map((row) => {
            const updatedRow = {
              ...row,
              [fieldKey]: parseFieldValue(field, writeValue),
            };

            // Check if the key is 'prescribedValue' and the value is null or empty
            if (fieldKey === 'prescribedValue' && (writeValue === null || writeValue === '')) {
              delete updatedRow.prescribedValue; // Remove the key
            }

            return updatedRow;
          });
        } else {
          // Only update the current row
          updatedRows = prevRows2.map((row, i) => {
            if (i === index2) {
              const updatedRow = {
                ...row,
                [fieldKey]: parseFieldValue(field, writeValue),
              };

              // Check if the key is 'prescribedValue' and the value is null or empty
              if (fieldKey === 'prescribedValue' && (writeValue === null || writeValue === '')) {
                delete updatedRow.prescribedValue; // Remove the key
              }

              return updatedRow;
            }
            return row;
          });
        }
      } else {
        // Normal update - only change the current row
        updatedRows = prevRows2.map((row, i) => {
          if (i === index2) {
            const updatedRow = {
              ...row,
              [fieldKey]: parseFieldValue(field, writeValue),
            };

            // Check if the key is 'prescribedValue' and the value is null or empty
            if (fieldKey === 'prescribedValue' && (writeValue === null || writeValue === '')) {
              delete updatedRow.prescribedValue; // Remove the key
            }

            return updatedRow;
          }
          return row;
        });
      }

      // Check if any field has empty values before making API call
      const hasEmptyFields = updatedRows.some((row) => {
        const cellValue = row[fieldKey];
        return cellValue === null || cellValue === undefined || cellValue === '';
      });

      // Block API call only when the user is editing reps and their just-typed
      // token is invalid (e.g., "AMRAP"). Silent block — matches the existing
      // negative-number guards above; per-keystroke toasts would spam the UI.
      const editedRepsTokenIsInvalid = field === 'reps'
        && value !== '' && value !== null && value !== undefined
        && !normalizeRepToken(String(value), ['AMAP']).isValid;

      // Only call the API if no fields are empty
      if (!hasEmptyFields && !editedRepsTokenIsInvalid) {
        // Debounce backend update with updated rows
        debouncedHandleUpdateSAQCItem(
          updatedRows,
          rest,
          assignedPace,
          intensityUnit,
          coachCompletion,
          completionOnly,
          content,
          index,
          nonSwappedItemFromParentCalendar,
          restPerRepUnit,
        );
      }

      return updatedRows;
    });
  };

  // Helper function to validate key input for numbers-only fields
  // Pass allowRangeNotation=true for prescribed value, rest per rep, and intensity fields
  const isValidNumberKey = (key, currentValue, allowRangeNotation = false) => {
    // Allow navigation and editing keys
    if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(key)) {
      return true;
    }

    // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
    if (key === 'a' || key === 'c' || key === 'v' || key === 'x' || key === 'z') {
      return true; // These will be handled by the browser with Ctrl modifier
    }

    // Allow decimal point; for range fields allow one per segment (split by '-')
    if (key === '.') {
      if (allowRangeNotation) {
        const currentSegment = currentValue.split('-').pop();
        if (!currentSegment.includes('.')) return true;
      } else if (!currentValue.includes('.')) {
        return true;
      }
    }

    // Allow digits
    if (/^\d$/.test(key)) {
      return true;
    }

    // Allow dash (range) and plus notation for range fields
    if (allowRangeNotation && (key === '-' || key === '+')) {
      return true;
    }

    return false;
  };

  // Helper function to validate key input for reps fields (allows AMAP and ranges)
  const isValidRepsKey = (key) => {
    // Allow navigation and editing keys
    if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(key)) {
      return true;
    }

    // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
    if (key === 'a' || key === 'c' || key === 'v' || key === 'x' || key === 'z') {
      return true; // These will be handled by the browser with Ctrl modifier
    }

    // Allow digits
    if (/^\d$/.test(key)) {
      return true;
    }

    // Allow letters for AMAP
    if (/^[a-zA-Z]$/.test(key)) {
      return true;
    }

    // Allow dash for ranges (8-12) and plus for minimums (8+)
    if (key === '-' || key === '+') {
      return true;
    }

    return false;
  };

  const columnInputResolver = (name, rowIndex, row, key) => {
    const fieldValue = row[keySwapDictionary[name]];
    if (name === 'rest') {
      return (
        <RowValueContainer key={key}>
          <RowValue
            data-add-focus={`${content.assignedId}-rest`}
            type='text'
            inputMode='numeric'
            value={rest}
            onChange={(e) => setRestFunction(e.target.value)}
            onFocus={(e) => e.target.select()}
            onKeyDown={(e) => {
              // Prevent commas and validate for numbers-only field
              if (e.key === ',' || (!e.ctrlKey && !isValidNumberKey(e.key, e.target.value))) {
                e.preventDefault();
              }
            }}
          />
        </RowValueContainer>
      );
    }
    if (name === 'athlete input') {
      return (
        <RowValueContainer key={key}>
          <RowValue
            placeholder='-'
            /** disable the ability to edit this field */
            disabled
            textColor={colors.textPlaceholder}
          />
        </RowValueContainer>
      );
    }

    const isRangeField = RANGE_FIELDS.has(name) || name.includes('intensity') || name.includes('rest per rep');
    return (
      <RowValueContainer key={key}>
        <RowValue
          data-add-focus={rowIndex === 0 && name !== 'velocity' ? `${content.assignedId}-${name}` : undefined}
          type='text' // Change to "text" to allow non-numerical inputs
          aria-label={`${name} for set ${rowIndex + 1}`}
          value={fieldValue ?? ''} // null/undefined -> '' (stay controlled); keep legit 0
          onChange={(e) => {
            handleInputChange(rowIndex, name, e.target.value);
          }}
          onFocus={(e) => e.target.select()}
          onKeyDown={(e) => {
            // Always prevent commas
            if (e.key === ',') {
              e.preventDefault();
              return;
            }

            // Skip validation for disabled fields
            if (name === 'velocity') {
              return;
            }

            // If the user is mutating an AMAP token they're sitting in the middle
            // of, wipe the whole token.
            if (name === 'reps') {
              const { value, selectionStart, selectionEnd } = e.target;
              const wipeResult = wipeRepKeywordOnMutation(
                e,
                value,
                selectionStart,
                selectionEnd,
                ['AMAP'],
                { backspaceDeletes: true },
              );
              if (wipeResult.wiped) {
                e.preventDefault();
                e.target.value = wipeResult.newValue;
                handleInputChange(rowIndex, name, wipeResult.newValue);
                const inputEl = e.target;
                requestAnimationFrame(() => {
                  inputEl.setSelectionRange(wipeResult.newCursor, wipeResult.newCursor);
                });
                return;
              }

              // Single-cell input: once the cell holds AMAP, block any
              // printable-char keystroke that would append/prepend (e.g.,
              // produce "AMAP8"). Wipe-on-edit was handled above; a full-token
              // selection also reaches here (deliberately allowed above), so
              // only block the no-selection append/prepend case.
              const cellTrimmed = (value || '').trim().toUpperCase();
              if (cellTrimmed === 'AMAP'
                  && selectionStart === selectionEnd
                  && typeof e.key === 'string'
                  && e.key.length === 1
                  && !e.ctrlKey && !e.metaKey) {
                e.preventDefault();
                return;
              }
            }

            // Handle AMAP autocomplete for reps field - when typing 'a' in an empty field,
            // or one whose entire value is selected (e.g. via select-on-focus)
            if (name === 'reps' && e.key.toLowerCase() === 'a' && (!e.target.value || e.target.value === '' || (e.target.selectionStart === 0 && e.target.selectionEnd === e.target.value.length))) {
              e.preventDefault();
              e.target.value = 'AMAP';
              handleInputChange(rowIndex, name, 'AMAP');
              return;
            }

            // Use different validation for reps vs other fields
            const isValid = name === 'reps'
              ? isValidRepsKey(e.key)
              : isValidNumberKey(e.key, e.target.value, isRangeField);

            // Allow Ctrl combinations
            if (!e.ctrlKey && !isValid) {
              e.preventDefault();
            }
          }}
          disabled={name === 'velocity'}
          /* Editable rows take RowValue's input surface; the read-only velocity row
             reads the page surface as an inset, the shared Input's disabled look. */
          background={name === 'velocity' ? colors.surface : undefined}
          data-focus-target={
            // Add focus target to the first editable field that's visible and in the first row
            rowIndex === 0 && (
              (name === 'reps' && primaryRowSwitch.reps)
              || (name === 'prescribed value' && primaryRowSwitch['prescribed value'] && !primaryRowSwitch.reps)
            )
              ? content.assignedId
              : undefined
          }
        />
      </RowValueContainer>
    );
  };

  const findCorrectUnit = (unitContent) => {
    if (unitContent?.exercise.unitAbbr) {
      return `(${unitContent?.exercise?.unitAbbr === 'wgt' ? 'lbs' : unitContent?.exercise?.unitAbbr})`;
    }
    return '';
  };

  /** Property label content (info icon + tooltips + SwapPropDropdownSAQC),
   * extracted so the transposed layout can place it in a label cell. */
  const renderPropertyLabel = (primaryRow) => {
    let refValue = null;
    if (primaryRow.name === 'reps') {
      refValue = targetRef;
    } else if (primaryRow.name === 'athlete input') {
      refValue = targetRefAthleteInputOnly;
    } else if (primaryRow.name === 'rest') {
      refValue = targetRefRest;
    }

    return (
      <NameAndDropdown ref={refValue}>
        {((primaryRow.name === 'reps') || (primaryRow.name === 'athlete input' && !completionOnly)) && (
        <IcomoonReact
          iconSet={iconSet}
          size={14}
          icon='info2'
          color={colors.textSecondary}
          style={{ cursor: 'pointer' }}
          onMouseEnter={() => {
            if (primaryRow.name === 'reps') {
              setShowTooltip(true);
            }
            if (primaryRow.name === 'athlete input') {
              setShowAthleteInputOnlyTooltip(true);
            }
          }}
          onMouseLeave={() => {
            if (primaryRow.name === 'reps') {
              setShowTooltip(false);
            }
            if (primaryRow.name === 'athlete input') {
              setShowAthleteInputOnlyTooltip(false);
            }
          }}
        />
        )}
        {((primaryRow.name === 'rest') && (
        <IcomoonReact
          iconSet={iconSet}
          size={14}
          icon='info2'
          color={colors.textSecondary}
          style={{ cursor: 'pointer' }}
          onMouseEnter={() => {
            if (primaryRow.name === 'rest') {
              setShowTooltipRest(true);
            }
          }}
          onMouseLeave={() => {
            if (primaryRow.name === 'rest') {
              setShowTooltipRest(false);
            }
          }}
        />
        ))}
        {primaryRow.name === 'reps' && showTooltip && (
        <TBportalTooltip targetRef={targetRef} backgroundColor='rgba(68, 68, 68, 1)' setShowToolTip={setShowTooltip} scrollContainerRefTopPosition={calendarContainerScrollPositionTop}>
          <TooltipDataContainer>
            <Text fontSize='13px' fontWeight={400} color='white'>Accepted Inputs</Text>
            <ul style={{ paddingLeft: '20px' }}>
              <li>
                <Text fontSize='13px' fontWeight={400} color='white'>Absolute (e.g. 5)</Text>
              </li>
              <li><Text fontSize='13px' fontWeight={400} color='white'>Range (e.g. 8-12)</Text></li>
              <li><Text fontSize='13px' fontWeight={400} color='white'>Minimum (e.g. 8+)</Text></li>
              <li><Text fontSize='13px' fontWeight={400} color='white'>&quot;AMAP&quot; for as many as possible</Text></li>
            </ul>
          </TooltipDataContainer>
        </TBportalTooltip>
        )}
        {primaryRow.name === 'athlete input' && showAthleteInputOnlyTooltip && !completionOnly && (
        <TBportalTooltip targetRef={targetRefAthleteInputOnly} backgroundColor='rgba(68, 68, 68, 1)' setShowToolTip={setShowAthleteInputOnlyTooltip} scrollContainerRefTopPosition={calendarContainerScrollPositionTop}>
          <TooltipDataContainerAthleteInput>
            <Text fontSize='13px' fontWeight={400} color='white'>Athlete input only</Text>
          </TooltipDataContainerAthleteInput>
        </TBportalTooltip>
        )}
        {primaryRow.name === 'rest' && showTooltipRest && (
        <TBportalTooltip targetRef={targetRefRest} backgroundColor='rgba(68, 68, 68, 1)' setShowToolTip={setShowTooltipRest} scrollContainerRefTopPosition={calendarContainerScrollPositionTop}>
          <TooltipDataContainerAthleteInput>
            <Text fontSize='13px' fontWeight={400} color='white'>Rest value will be the same for all sets.</Text>
          </TooltipDataContainerAthleteInput>
        </TBportalTooltip>
        )}
        <SwapPropDropdownSAQC
          visible={primaryRow.name !== 'athlete input'}
          setPrimaryRowSwitch={setPrimaryRowSwitch}
          primaryRowSwitch={primaryRowSwitch}
          rowName={primaryRow.name}
          size={12}
          updateColumnName={updateColumnName}
          setIntensityUnit={setIntensityUnitAndUpdate}
          setRestPerRepUnit={setRestPerRepUnitAndUpdate}
          name={(primaryRow?.label || capitalizeWords(primaryRow.name))}
          unit={primaryRow.name === 'prescribed value' ? findCorrectUnit(content) : null}
        />
      </NameAndDropdown>
    );
  };

  return (
    <Wrapper
      ref={tableContainerRef}
      onBlur={nonSwappedItemFromParentCalendar
        ? () => flushDebounceForTable(content.assignedId)
        : undefined}
    >
      <SetsCountHeader>
        <SetsFieldGroup>
          <SetsCountLabel>Sets</SetsCountLabel>
          <SetsStepper>
            <StepperButton
              type='button'
              aria-label='Decrease sets'
              onClick={() => setSetCount(rows.length - 1)}
              disabled={rows.length <= 1}
            >
              −
            </StepperButton>
            <StepperInput
              type='text'
              inputMode='numeric'
              value={setsDraft !== null ? setsDraft : rows.length}
              onFocus={(e) => {
                setSetsDraft(String(rows.length));
                e.target.select();
              }}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^0-9]/g, '');
                if (raw === '') {
                  setSetsDraft('');
                  return;
                }
                let value = parseInt(raw, 10);
                if (value > 10) value = parseInt(raw.slice(-1), 10);
                setSetsDraft(String(Math.min(10, Math.max(1, value))));
              }}
              onBlur={commitSetsDraft}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.target.blur();
                }
              }}
              aria-label='Number of sets'
            />
            <StepperButton
              type='button'
              aria-label='Increase sets'
              onClick={() => setSetCount(rows.length + 1)}
              disabled={rows.length >= 10}
            >
              +
            </StepperButton>
          </SetsStepper>
          <SetsSubtext># of Sets: 1-10</SetsSubtext>
        </SetsFieldGroup>
        <StatusBadgeScroller>
          {coachCompletion && <StateBadge>Coach Completion</StateBadge>}
          {completionOnly && <StateBadge>Completion Only</StateBadge>}
        </StatusBadgeScroller>
      </SetsCountHeader>

      <TransposedScroll>
        <TransposedTable>
          <TransposedHeaderRow>
            <PropertyLabelCell />
            {rows.map((_, setIdx) => (
              <SetHeaderCell key={`set-header-${setIdx}`}>
                {`Set ${setIdx + 1}`}
              </SetHeaderCell>
            ))}
          </TransposedHeaderRow>

          {primaryRows.map((primaryRow) => {
            if (primaryRow.name === 'sets') return null;
            if (!primaryRowSwitch[primaryRow.name]) return null;
            return (
              <TransposedRow key={`prop-${primaryRow.name}`}>
                <PropertyLabelCell>
                  {renderPropertyLabel(primaryRow)}
                </PropertyLabelCell>
                {rows.map((row, rowIndex) => (
                  <SetCell key={`cell-${primaryRow.name}-${rowIndex}`}>
                    {columnInputResolver(primaryRow.name, rowIndex, row, `${primaryRow.name}-${rowIndex}`)}
                  </SetCell>
                ))}
              </TransposedRow>
            );
          })}
        </TransposedTable>
      </TransposedScroll>

      <TransposedFooter>
        {showAddColumnButton && (
          <StaticAddColumnDropdownComponent
            primaryRows={primaryRows}
            setPrimaryRows={setPrimaryRows}
            primaryRowSwitch={primaryRowSwitch}
            setPrimaryRowSwitch={setPrimaryRowSwitch}
            setIntensityUnit={setIntensityUnit}
            setRestPerRepUnit={setRestPerRepUnit}
            setPrimaryRowSwitchState={setPrimaryRowSwitchState}
            size={12}
            containerRef={tableContainerRef}
            assignedId={content.assignedId}
            closeOnSelect
            onPropertyAdded={focusAddedProperty}
            trigger={(
              <AddPropertyTrigger>
                <AddPropertyTriggerIcon>
                  <IcomoonReact iconSet={iconSet} size={10} icon='plus' color='white' />
                </AddPropertyTriggerIcon>
                Add Property
              </AddPropertyTrigger>
            )}
          />
        )}
      </TransposedFooter>
    </Wrapper>
  );
};

ExerciseTableTransposedSAQC.defaultProps = {
  isRecurring: '',
  assignedPace: '',
  date: null,
  dayNumber: null,
};

ExerciseTableTransposedSAQC.propTypes = {
  content: PropTypes.instanceOf(Object).isRequired,
  swapDropdownSelectedExercise: PropTypes.instanceOf(Object).isRequired,
  index: PropTypes.number.isRequired,
  coachCompletion: PropTypes.bool.isRequired,
  completionOnly: PropTypes.bool.isRequired,
  nonSwappedItemFromParentCalendar: PropTypes.bool.isRequired,
  isRecurring: PropTypes.string,
  assignedPace: PropTypes.string,
  measure: PropTypes.func.isRequired,
  date: PropTypes.string,
  dayNumber: PropTypes.number,
};

export default ExerciseTableTransposedSAQC;
